import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useCreateMessage, useGetMessageWith, useMarkMessageRead } from '_api';
import { Message, MessagePayload } from '_api/types';
import { SEVERITY } from '_constants/derived';
import { AttachedFile } from 'components/atoms';
import {
  AlertMessage,
  FileAttachmentButton,
  Message as ChatMessage,
  ViewProfileMenu,
} from 'components/molecules';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoMdSend } from 'react-icons/io';
import { useMessage } from 'utils/hooks';

import { ChatProps } from './types';

export const Chat = ({ correspondentId }: ChatProps) => {
  const { mutateAsync: createMessage } = useCreateMessage();
  const { data: dataMessages, refetch } = useGetMessageWith(correspondentId);
  const { mutateAsync: markMessageRead } = useMarkMessageRead();
  const { getPatientsByGuardian } = useMessage();
  const [messages, setMessages] = useState<Message[] | undefined>([] as Message[]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [filename, setFilename] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [fileAttachment, setFileAttachment] = useState<string | undefined>(undefined);
  const [disabledSubmit, setDisabledSubmit] = useState(correspondentId === undefined);
  const guardianPatients = useMemo(
    () => getPatientsByGuardian(correspondentId),
    [correspondentId],
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (correspondentId && dataMessages) {
      setMessages(dataMessages);
    }
    setDisabledSubmit(correspondentId === undefined);
  }, [dataMessages]);

  useEffect(() => {
    const readMessage = async (messageId: number) => await markMessageRead({ messageId });

    if (messages && messages.length > 0) {
      messages.forEach((message) => {
        if (message.sender.id === correspondentId && message.status === 'SENT')
          readMessage(message.id);
      });
    }
  }, [messages]);

  useEffect(() => {
    if (correspondentId) {
      setLoadingMessages(true);
      refetch().then(() => setLoadingMessages(false));
    }
  }, [correspondentId, refetch]);

  const handleFileSelect = (file: string) => {
    setFilename(file);
  };

  const handleFileRemove = () => {
    setFilename('');
    setFileAttachment(undefined);
  };

  const reset = () => {
    handleFileRemove();
    setNewMessage('');
    setDisabledSubmit(false);
  };

  const handleSendMessage = () => {
    if (correspondentId) {
      setDisabledSubmit(true);
      const payload = {
        receiverId: correspondentId,
        message: newMessage,
        filename,
        type: filename && filename.length > 0 ? 'FILE' : 'TEXT',
      } as MessagePayload;
      createMessage(payload)
        .then(reset)
        .catch((err) => {
          setErrorMessage(err.message);
          setDisabledSubmit(false);
        });
    }
  };

  const keyInputListener = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <Grid
      item
      md={8}
      sm={12}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {guardianPatients.length > 0 ? (
        <ViewProfileMenu patients={guardianPatients} />
      ) : null}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          padding: 5,
        }}
      >
        {messages === undefined || loadingMessages === true ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '25%',
            }}
          >
            <Typography variant="body2" color="gray" sx={{ marginBottom: '10px' }}>
              Fetching Messages...
            </Typography>
            <CircularProgress
              sx={{
                height: '60px',
                width: '60px',
              }}
            />
          </Box>
        ) : (
          messages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              isOwnMessage={
                correspondentId ? message.sender.id !== correspondentId : false
              }
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>
      <Box sx={{ padding: 5, borderTop: '1px solid #ddd' }}>
        {fileAttachment && (
          <AttachedFile filename={fileAttachment} onRemove={handleFileRemove} />
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: 5,
          }}
        >
          <TextField
            fullWidth
            autoComplete="off"
            placeholder="Write a message..."
            value={newMessage}
            disabled={disabledSubmit}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={keyInputListener}
          />
          <FileAttachmentButton
            disabled={disabledSubmit}
            onFileSelect={handleFileSelect}
            isUploadingFile={setDisabledSubmit}
            setFileName={setFileAttachment}
          />
          {newMessage.length !== 0 || filename ? (
            <IconButton
              disabled={disabledSubmit}
              onClick={handleSendMessage}
              color="primary"
            >
              <IoMdSend />
            </IconButton>
          ) : (
            <IconButton disabled={true} onClick={handleSendMessage} color="primary">
              <IoMdSend />
            </IconButton>
          )}
        </Box>
      </Box>
      <AlertMessage
        open={!!errorMessage}
        onClose={() => setErrorMessage('')}
        severity={SEVERITY.error}
        message={
          <>
            Failed to send message
            <br />
            {errorMessage}
            <br />
            Resolve issues and try again.
          </>
        }
      />
    </Grid>
  );
};
